import React, { useState, useEffect, useRef } from 'react';
import { cemeteryAPI, mediaUploadAPI } from '../services/api';
import './MediaUploadPage.css';
import Toast from '../components/Toast';
import CemeteryContextBar from '../components/CemeteryContextBar';

function exportFilesCSV(files, cemetery, path) {
  const dir = path ? `${cemetery}/${path}` : cemetery;
  const rows = [
    'filename,directory,public_url',
    ...files.map(f => {
      const name = (f.name || '').replace(/"/g, '""');
      const url = (f.url || '').replace(/"/g, '""');
      return `"${name}","${dir}","${url}"`;
    }),
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${cemetery}_${path ? path.replace(/\//g, '_') + '_' : ''}files.csv`;
  link.click();
  URL.revokeObjectURL(link.href);
}

function MediaUploadPage() {
  const [cemeteries, setCemeteries] = useState([]);
  const [selectedCemetery, setSelectedCemetery] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [browsePath, setBrowsePath] = useState([]);
  const [subDirs, setSubDirs] = useState([]);
  const [loadingDirs, setLoadingDirs] = useState(false);
  const [selectedPath, setSelectedPath] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [fileProgress, setFileProgress] = useState({});
  const [toast, setToast] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [uploadResults, setUploadResults] = useState([]);
  const [browseFiles, setBrowseFiles] = useState([]);
  // Move-from-source state
  const [sourcePreview, setSourcePreview] = useState(null);
  const [loadingSource, setLoadingSource] = useState(false);
  const [movingSource, setMovingSource] = useState(false);
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);

  useEffect(() => {
    loadCemeteries();
  }, []);

  useEffect(() => {
    if (selectedCemetery) {
      loadSubDirs([]);
    }
  }, [selectedCemetery]);

  const loadCemeteries = async () => {
    try {
      const response = await cemeteryAPI.list();
      setCemeteries(response.data.cemeteries || response.data);
    } catch (err) {
      console.error('Failed to load cemeteries:', err);
    }
  };

  const loadSubDirs = async (pathSegments) => {
    try {
      setLoadingDirs(true);
      const pathStr = pathSegments.join('/');
      const [dirsRes, filesRes] = await Promise.all([
        mediaUploadAPI.browseDirectories(selectedCemetery, pathStr),
        mediaUploadAPI.browseFiles(selectedCemetery, pathStr),
      ]);
      setSubDirs(dirsRes.data.dirs || []);
      setBrowseFiles(filesRes.data.files || []);
      setBrowsePath(pathSegments);
      // If no subdirs exist, auto-select this path for upload
      if (!dirsRes.data.dirs || dirsRes.data.dirs.length === 0) {
        setSelectedPath(pathStr);
      }
    } catch (err) {
      console.error('Failed to load directories:', err);
      setToast({ type: 'error', text: 'Failed to load directories' });
    } finally {
      setLoadingDirs(false);
    }
  };

  const filteredCemeteries = cemeteries.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.unique_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const currentPath = browsePath.join('/');

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = Array.from(e.dataTransfer.files);
    setFiles(prev => [...prev, ...dropped]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleFileSelect = (e) => {
    const selected = Array.from(e.target.files);
    setFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const handleFolderSelect = (e) => {
    const selected = Array.from(e.target.files).map(file => {
      // webkitRelativePath is "topFolder/sub/file.txt" — strip the top-level folder
      const relPath = file.webkitRelativePath;
      const withoutTop = relPath ? relPath.split('/').slice(1).join('/') : '';
      file._relativePath = withoutTop || file.name;
      return file;
    });
    setFiles(prev => [...prev, ...selected]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleNavigate = (dirName) => {
    const newPath = [...browsePath, dirName];
    loadSubDirs(newPath);
  };

  const handleBreadcrumbClick = (index) => {
    const newPath = browsePath.slice(0, index);
    loadSubDirs(newPath);
  };

  const handleUploadHere = () => {
    setSelectedPath(currentPath);
  };

  const handleUpload = async () => {
    if (!selectedCemetery || selectedPath === null || files.length === 0) return;

    setUploading(true);
    setFileProgress({});
    setUploadResults([]);

    try {
      const relativePaths = files.map(f => f._relativePath || '');
      const response = await mediaUploadAPI.uploadFilesAsync(
        selectedCemetery,
        selectedPath,
        files,
        relativePaths,
      );

      setToast({
        type: 'success',
        text: `Upload task submitted (${response.data.file_count} files). Track on Tasks page.`,
      });
      setFiles([]);
      setFileProgress({});
      // Refresh current directory view
      loadSubDirs(browsePath);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setToast({ type: 'error', text: `Upload failed: ${detail}` });
    } finally {
      setUploading(false);
    }
  };

  // --- Move from Source ---
  const handlePreviewSource = async () => {
    setLoadingSource(true);
    setSourcePreview(null);
    try {
      const res = await mediaUploadAPI.listSourceFiles(selectedCemetery);
      setSourcePreview(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setToast({ type: 'error', text: `Failed to list source files: ${detail}` });
    } finally {
      setLoadingSource(false);
    }
  };

  const handleMoveSource = async () => {
    if (!window.confirm(
      `Move ${sourcePreview.total_count} files (${sourcePreview.total_size_mb} MB) from source bucket to storage?`
    )) return;

    setMovingSource(true);
    try {
      const res = await mediaUploadAPI.moveFromSource(selectedCemetery);
      setToast({
        type: 'success',
        text: `Move task submitted (ID: ${res.data.task_id}). Track on Tasks page.`,
      });
      setSourcePreview(null);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      setToast({ type: 'error', text: `Move failed: ${detail}` });
    } finally {
      setMovingSource(false);
    }
  };

  return (
    <div className="media-upload-page">
      {toast && <Toast type={toast.type} message={toast.text} onClose={() => setToast(null)} />}

      <CemeteryContextBar
        cemeteryUniqueName={selectedCemetery}
        cemeteryName={cemeteries.find(c => c.unique_name === selectedCemetery)?.name}
        onChange={() => setSelectedCemetery('')}
      />
      <div className="page-header">
        <h1>Media Upload</h1>
        <p className="page-description">Upload files directly to R2 storage for a cemetery.</p>
      </div>

      {/* Step 1: Cemetery Selection */}
      <div className="card">
        <div className="step-section">
          <h3>1. Select Cemetery</h3>
          {!selectedCemetery ? (
            <>
              <input
                type="text"
                placeholder="Search cemeteries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              <div className="cemetery-list" style={{ maxHeight: '300px', overflowY: 'auto', marginTop: '8px' }}>
                {filteredCemeteries.map(c => (
                  <div
                    key={c.unique_name}
                    className="cemetery-item"
                    onClick={() => { setSelectedCemetery(c.unique_name); setSearchQuery(''); setSelectedPath(null); setSourcePreview(null); }}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid var(--border-color)' }}
                  >
                    <strong>{c.name}</strong>
                    <span style={{ marginLeft: '8px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                      {c.unique_name}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span><strong>{selectedCemetery}</strong></span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => { setSelectedCemetery(''); setBrowsePath([]); setBrowseFiles([]); setSelectedPath(null); setFiles([]); setUploadResults([]); setSourcePreview(null); }}
              >
                Change
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Move from Source Bucket */}
      {selectedCemetery && (
        <div className="card">
          <div className="step-section">
            <h3>Move from Source Bucket</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
              Move files from the temp-appspot source bucket into storage for <strong>{selectedCemetery}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                className="btn btn-secondary"
                onClick={handlePreviewSource}
                disabled={loadingSource}
              >
                {loadingSource ? 'Checking...' : 'Preview Source Files'}
              </button>
              {sourcePreview && sourcePreview.total_count > 0 && (
                <button
                  className="btn btn-primary"
                  onClick={handleMoveSource}
                  disabled={movingSource}
                >
                  {movingSource ? 'Submitting...' : `Move ${sourcePreview.total_count} files (${sourcePreview.total_size_mb} MB)`}
                </button>
              )}
            </div>
            {sourcePreview && sourcePreview.total_count === 0 && (
              <p style={{ marginTop: '8px', color: 'var(--text-secondary)' }}>No files found in source bucket.</p>
            )}
          </div>
        </div>
      )}

      {/* Step 2: Directory Browser */}
      {selectedCemetery && (
        <div className="card">
          <div className="step-section">
            <h3>2. Browse & Select Directory</h3>

            {/* Breadcrumb Navigation */}
            <div style={{ marginBottom: '16px', padding: '8px 12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px', fontSize: '13px' }}>
              <span style={{ cursor: 'pointer', color: 'var(--link)', textDecoration: 'underline' }} onClick={() => handleBreadcrumbClick(0)}>
                {selectedCemetery}
              </span>
              {browsePath.map((segment, idx) => (
                <span key={idx}>
                  <span style={{ margin: '0 4px' }}>/</span>
                  <span
                    style={{ cursor: 'pointer', color: 'var(--link)', textDecoration: 'underline' }}
                    onClick={() => handleBreadcrumbClick(idx + 1)}
                  >
                    {segment}
                  </span>
                </span>
              ))}
              {selectedPath !== null && selectedPath !== currentPath && (
                <span style={{ color: 'var(--text-secondary)', marginLeft: '12px' }}>
                  → Selected: <code>{selectedPath}</code>
                </span>
              )}
            </div>

            {/* Directory List */}
            {loadingDirs ? (
              <p style={{ color: 'var(--text-secondary)' }}>Loading directories...</p>
            ) : (
              <>
                {subDirs.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                    {subDirs.map(dirName => (
                      <button
                        key={dirName}
                        className="dir-btn"
                        onClick={() => handleNavigate(dirName)}
                        style={{ cursor: 'pointer' }}
                      >
                        📁 {dirName}
                      </button>
                    ))}
                    <button
                      className="dir-btn"
                      onClick={() => setShowNewFolderInput(prev => !prev)}
                      style={{ cursor: 'pointer' }}
                    >
                      + New Folder
                    </button>
                  </div>
                ) : (
                  <div style={{ marginBottom: '16px' }}>
                    <p style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>No subdirectories found.</p>
                    <button
                      className="dir-btn"
                      onClick={() => setShowNewFolderInput(prev => !prev)}
                      style={{ cursor: 'pointer' }}
                    >
                      + New Folder
                    </button>
                  </div>
                )}
                {showNewFolderInput && (
                  <div className="custom-dir-input" style={{ marginBottom: '16px' }}>
                    <input
                      type="text"
                      placeholder="Folder name..."
                      value={newFolderName}
                      onChange={(e) => setNewFolderName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          const name = newFolderName.trim();
                          if (name && !name.includes('/')) {
                            handleNavigate(name);
                            setNewFolderName('');
                            setShowNewFolderInput(false);
                          }
                        }
                      }}
                    />
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        const name = newFolderName.trim();
                        if (!name) return;
                        if (name.includes('/')) {
                          setToast({ type: 'error', text: 'Folder name cannot contain slashes' });
                          return;
                        }
                        handleNavigate(name);
                        setNewFolderName('');
                        setShowNewFolderInput(false);
                      }}
                    >
                      Create
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => { setShowNewFolderInput(false); setNewFolderName(''); }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}

            {/* Existing Files */}
            {!loadingDirs && browseFiles.length > 0 && (
              <div className="browse-files-list">
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '13px', color: 'var(--text-secondary)' }}>
                    Files in this directory ({browseFiles.length})
                  </h4>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => exportFilesCSV(browseFiles, selectedCemetery, currentPath)}
                  >
                    Download CSV
                  </button>
                </div>
                {browseFiles.map((f, i) => (
                  <div key={i} className="browse-file-item">
                    <span className="browse-file-name">{f.name}</span>
                    <span className="browse-file-size">{formatSize(f.size)}</span>
                    {f.url ? (
                      <a href={f.url} target="_blank" rel="noopener noreferrer" className="browse-file-link">
                        Download
                      </a>
                    ) : (
                      <span className="browse-file-size">No URL</span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Upload Here Button */}
            <button
              className="btn btn-primary"
              onClick={handleUploadHere}
              style={{ marginRight: '8px' }}
            >
              ✓ Upload to: <code>{currentPath || selectedCemetery}</code>
            </button>
          </div>
        </div>
      )}

      {/* Step 3: File Upload */}
      {selectedCemetery && selectedPath !== null && (
        <div className="card">
          <div className="step-section">
            <h3>3. Upload Files</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
              Target: <code>{selectedCemetery}/{selectedPath}/</code>
              {' '}&mdash; ZIP files will be extracted automatically.
            </p>

            <div
              className="dropzone"
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onClick={() => fileInputRef.current?.click()}
            >
              <p>Drag &amp; drop files here, or click to browse</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
            <input
              ref={folderInputRef}
              type="file"
              webkitdirectory=""
              style={{ display: 'none' }}
              onChange={handleFolderSelect}
            />
            <button
              className="btn btn-secondary"
              onClick={() => folderInputRef.current?.click()}
              style={{ marginTop: '8px' }}
            >
              Select Folder
            </button>

            {files.length > 0 && (
              <div className="file-queue">
                {files.map((file, i) => (
                  <div key={i} className="file-queue-item">
                    <div className="file-info">
                      <span className="file-name">{file._relativePath || file.name}</span>
                      <span className="file-size">{formatSize(file.size)}</span>
                    </div>
                    {fileProgress[i] !== undefined && fileProgress[i] < 100 && (
                      <div className="progress-bar-container">
                        <div className="progress-bar-fill" style={{ width: `${fileProgress[i]}%` }} />
                      </div>
                    )}
                    {fileProgress[i] === 100 && <span style={{ color: 'var(--success)' }}>Done</span>}
                    {!uploading && (
                      <button className="remove-btn" onClick={() => removeFile(i)}>&times;</button>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="upload-actions">
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={uploading || files.length === 0}
              >
                {uploading ? 'Submitting...' : `Upload ${files.length} file(s)`}
              </button>
              {files.length > 0 && !uploading && (
                <button className="btn btn-secondary" onClick={() => { setFiles([]); setUploadResults([]); }}>
                  Clear All
                </button>
              )}
            </div>

            {uploadResults.length > 0 && (
              <div style={{ marginTop: '16px', padding: '12px', backgroundColor: 'var(--bg-secondary)', borderRadius: '6px' }}>
                <h4 style={{ marginBottom: '8px' }}>Uploaded Files</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {uploadResults.map((r, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: '1px solid var(--border-color)', fontSize: '13px' }}>
                      <span>{r.filename} ({formatSize(r.size)})</span>
                      {r.url ? (
                        <a href={r.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: '12px', whiteSpace: 'nowrap' }}>
                          Download
                        </a>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', marginLeft: '12px' }}>No public URL</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default MediaUploadPage;
